import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { api } from '../utils/api';
import {
  Container,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';

const SessionRetroPage = () => {
  const { sessionId } = useParams();
  const [retroData, setRetroData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchRetroData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/sessions/${sessionId}/retro`);
      setRetroData(response.data);
    } catch (err) {
      console.error('Error fetching retro data:', err);
      setError(err.response?.data?.message || `Failed to load retro data for session ${sessionId}.`);
    }
    setLoading(false);
  }, [sessionId]);

  useEffect(() => {
    fetchRetroData();
  }, [fetchRetroData]);

  const handleEditClick = (item) => {
    setEditingItemId(item.id);
    setEditValue(item.actualPoints || '');
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditValue('');
  };

  const handleSaveActualPoints = async (itemId) => {
    setSaving(true);
    try {
      await api.put(`/sessions/${sessionId}/retro/items/${itemId}`, {
        actualPoints: editValue || null,
      });
      await fetchRetroData();
      setEditingItemId(null);
      setEditValue('');
    } catch (err) {
      console.error('Error saving actual points:', err);
      setError(err.response?.data?.message || 'Failed to save actual points.');
    }
    setSaving(false);
  };

  const getVarianceColor = (variance) => {
    if (variance === null) return 'default';
    if (variance === 0) return 'success';
    if (Math.abs(variance) <= 2) return 'warning';
    return 'error';
  };

  const getVarianceLabel = (variance, variancePercent) => {
    if (variance === null) return '-';
    const sign = variance > 0 ? '+' : '';
    return `${sign}${variance} (${sign}${variancePercent}%)`;
  };

  if (loading) {
    return (
      <Container sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!retroData) {
    return (
      <Container sx={{ py: 3 }}>
        <Alert severity="info">No retro data found for this session.</Alert>
      </Container>
    );
  }

  const { sessionName, sessionDate, items, summary } = retroData;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <IconButton component={RouterLink} to="/dashboard" aria-label="back to dashboard" sx={{ mb: 2 }}>
        <ArrowBackIcon />
      </IconButton>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Sprint Retro: {sessionName}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          Session Date: {new Date(sessionDate).toLocaleDateString()}
        </Typography>

        {/* Summary Stats */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
          <Paper variant="outlined" sx={{ p: 2, minWidth: 150 }}>
            <Typography variant="body2" color="text.secondary">Total Estimated</Typography>
            <Typography variant="h5">{summary.totalEstimated}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, minWidth: 150 }}>
            <Typography variant="body2" color="text.secondary">Total Actual</Typography>
            <Typography variant="h5">{summary.totalActual}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, minWidth: 150 }}>
            <Typography variant="body2" color="text.secondary">Items with Actuals</Typography>
            <Typography variant="h5">{summary.itemsWithActual} / {summary.totalItems}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, minWidth: 150 }}>
            <Typography variant="body2" color="text.secondary">Accuracy</Typography>
            <Typography variant="h5">
              {summary.accuracyPercent !== null ? `${summary.accuracyPercent}%` : '-'}
            </Typography>
          </Paper>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h5" gutterBottom>
          Items ({items?.length || 0})
        </Typography>

        {items && items.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table sx={{ minWidth: 650 }} aria-label="retro items table">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="center">Estimated</TableCell>
                  <TableCell align="center">Actual</TableCell>
                  <TableCell align="center">Variance</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell component="th" scope="row">
                      <Box>
                        <Typography variant="body1">{item.title}</Typography>
                        {item.jiraKey && (
                          <Typography variant="caption" color="text.secondary">
                            {item.jiraKey}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={item.finalEstimate || '-'} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      {editingItemId === item.id ? (
                        <TextField
                          size="small"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="Points"
                          sx={{ width: 80 }}
                          autoFocus
                          disabled={saving}
                        />
                      ) : (
                        <Chip
                          label={item.actualPoints || '-'}
                          size="small"
                          color={item.actualPoints ? 'primary' : 'default'}
                          variant={item.actualPoints ? 'filled' : 'outlined'}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={getVarianceLabel(item.variance, item.variancePercent)}
                        size="small"
                        color={getVarianceColor(item.variance)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {editingItemId === item.id ? (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleSaveActualPoints(item.id)}
                            disabled={saving}
                            color="success"
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleCancelEdit}
                            disabled={saving}
                            color="error"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(item)}
                          aria-label="edit actual points"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography color="text.secondary">
            No estimated items found for this session.
          </Typography>
        )}
      </Paper>
    </Container>
  );
};

export default SessionRetroPage;
